import React from 'react';
import {
  isAtlassianCloud,
  isCommercialOrAcademicOrFreeType,
  isCommercialOrAcademicType,
  isEvaluationType,
  isFreeType,
  isFutureEndDate,
  isNextMonthFutureDate,
  isNextMonthPastDate,
  isSupportOnlyProductKey,
  isNotMoreThat5DaysOverdue,
  isPastEndDate,
  notPartOfForRenewKeys,
  isHosted,
  hasActiveStatus,
} from 'util/mac/helpers';
import { FreeExtensionAppliedMessage } from './FreeExtensionAppliedMessage';
import { StyledButtonsWrapper, StyledFlex, StyledFlexColumnWrapper } from '../StyledComponents';
import { ActionsMessage } from './ActionsMessage';
import { ClaimYourSiteLink } from './ClaimYourSiteLink';
import { ArchiveUnarchiveButtonLink } from './ArchiveUnarchiveButtonLink';
import { NeedSiteAdministratorMessage } from './NeedSiteAdministratorMessage';
import { ProductAdminLink } from './ProductAdminLink';
import { ExpiredMessage } from './ExpiredMessage';
import { BuyLink } from './BuyLink';
import { RenewLink } from './RenewLink';
import Progress from 'redux-progress';
import { OpsgenieActionMessage } from './OpsgenieActionMessage';
import { BitbucketActionMessage } from './BitbucketActionMessage';

type HostedNonAtlassianOrganizationAccountProps = {
  isSiteOnConcession: boolean;
  isArchiveButtonVisible: boolean;
  isArchived: boolean;
  isAccount: boolean;
  productKey: string;
  isRenewalActionNone: boolean;
  isRenewableAccount: Progress<boolean>;
  accountDetails: any;
};

const hasProductInChildren = (children, childProductKey: string) =>
  children.find(
    (account) =>
      account.merchandise.productPackage.key === childProductKey && hasActiveStatus(account.status),
  ) != null;

const UnclaimedCloudSiteSection = ({
  isSiteOnConcession,
  isArchiveButtonVisible,
  isArchived,
  accountId,
  isAccount,
  externalUuid,
}: {
  isSiteOnConcession: boolean;
  isArchiveButtonVisible: boolean;
  isArchived: boolean;
  accountId: number;
  isAccount: boolean;
  externalUuid: string;
}) => (
  <>
    {isSiteOnConcession && <FreeExtensionAppliedMessage />}
    <StyledFlex>
      <ActionsMessage />
      <StyledButtonsWrapper>
        <ClaimYourSiteLink externalUuid={externalUuid} />
        <ArchiveUnarchiveButtonLink
          isArchiveButtonVisible={isArchiveButtonVisible}
          isArchived={isArchived}
          accountId={accountId}
          isAccount={isAccount}
        />
      </StyledButtonsWrapper>
    </StyledFlex>
  </>
);

const IsEvaluationOrAutoRenewSection = ({
  productKey,
  shouldDisplayOpsgenieBanner,
  shouldDisplayBitbucketBanner,
  accountName,
  isSiteOnConcession,
  endDate,
  domain,
  hasExternalUuid,
  isArchiveButtonVisible,
  isArchived,
  accountId,
  isAccount,
}: {
  productKey: string;
  shouldDisplayOpsgenieBanner: boolean;
  shouldDisplayBitbucketBanner: boolean;
  accountName: string;
  isSiteOnConcession: boolean;
  endDate: any;
  domain: string;
  hasExternalUuid: boolean;
  isArchiveButtonVisible: boolean;
  isArchived: boolean;
  accountId: number;
  isAccount: boolean;
}) => (
  <>
    <StyledFlexColumnWrapper>
      {isAtlassianCloud(productKey) && <NeedSiteAdministratorMessage />}
      {shouldDisplayOpsgenieBanner && <OpsgenieActionMessage />}
      {shouldDisplayBitbucketBanner && <BitbucketActionMessage accountName={accountName} />}
    </StyledFlexColumnWrapper>
    {isSiteOnConcession && <FreeExtensionAppliedMessage />}
    <StyledFlex>
      <ActionsMessage />
      <StyledButtonsWrapper>
        {isNextMonthFutureDate(endDate) && (
          <ProductAdminLink
            domain={domain}
            accountName={accountName}
            productKey={productKey}
            hasExternalUuid={hasExternalUuid}
          />
        )}
        {isPastEndDate(endDate) && <ExpiredMessage />}
        <BuyLink productKey={productKey} />
        <ArchiveUnarchiveButtonLink
          isArchiveButtonVisible={isArchiveButtonVisible}
          isArchived={isArchived}
          accountId={accountId}
          isAccount={isAccount}
        />
      </StyledButtonsWrapper>
    </StyledFlex>
  </>
);

export const HostedNonAtlassianOrganizationAccount = ({
  isSiteOnConcession,
  isArchiveButtonVisible,
  isArchived,
  isAccount,
  productKey,
  isRenewalActionNone,
  isRenewableAccount,
  accountDetails,
}: HostedNonAtlassianOrganizationAccountProps) => {
  const {
    isUnclaimedCloudSite,
    id: accountId,
    renewalAction,
    externalUuid,
    name: accountName,
    domain,
    latestPeriod: { endDate },
    merchandise: {
      details: { licenseType },
      productEdition: { monthsValid },
      productPackage: { productType },
    },
    children,
  } = accountDetails;
  const hasExternalUuid = !!externalUuid;

  const shouldDisplayProductBanners =
    ((isEvaluationType(licenseType) && renewalAction === 'AUTO_RENEW') ||
      isCommercialOrAcademicOrFreeType(licenseType)) &&
    isHosted(productType) &&
    isNextMonthFutureDate(endDate) &&
    !isRenewalActionNone &&
    hasExternalUuid &&
    isAtlassianCloud(productKey);

  const shouldDisplayOpsgenieBanner =
    shouldDisplayProductBanners && hasProductInChildren(children, 'opsgenie');

  const shouldDisplayBitbucketBanner =
    shouldDisplayProductBanners && hasProductInChildren(children, 'com.atlassian.bitbucket');

  if (isUnclaimedCloudSite && isFutureEndDate(endDate)) {
    return (
      <UnclaimedCloudSiteSection
        isSiteOnConcession={isSiteOnConcession}
        isArchiveButtonVisible={isArchiveButtonVisible}
        isArchived={isArchived}
        accountId={accountId}
        isAccount={isAccount}
        externalUuid={externalUuid}
      />
    );
  }

  if (isEvaluationType(licenseType) && renewalAction === 'AUTO_RENEW') {
    return (
      <IsEvaluationOrAutoRenewSection
        productKey={productKey}
        shouldDisplayOpsgenieBanner={shouldDisplayOpsgenieBanner}
        shouldDisplayBitbucketBanner={shouldDisplayBitbucketBanner}
        accountName={accountName}
        isSiteOnConcession={isSiteOnConcession}
        endDate={endDate}
        domain={domain}
        hasExternalUuid={hasExternalUuid}
        isArchiveButtonVisible={isArchiveButtonVisible}
        isArchived={isArchived}
        accountId={accountId}
        isAccount={isAccount}
      />
    );
  }

  const shouldDisplayNeedSiteAdministratorBanner =
    isAtlassianCloud(productKey) &&
    isCommercialOrAcademicOrFreeType(licenseType) &&
    isNextMonthFutureDate(endDate) &&
    !isRenewalActionNone &&
    hasExternalUuid;

  if (isFreeType(licenseType) || isNotMoreThat5DaysOverdue(endDate)) {
    return (
      <>
        <StyledFlexColumnWrapper>
          {shouldDisplayNeedSiteAdministratorBanner && <NeedSiteAdministratorMessage />}
          {shouldDisplayOpsgenieBanner && <OpsgenieActionMessage />}
          {shouldDisplayBitbucketBanner && <BitbucketActionMessage accountName={accountName} />}
        </StyledFlexColumnWrapper>
        {isSiteOnConcession && <FreeExtensionAppliedMessage />}
        <StyledFlex>
          <ActionsMessage />
          <StyledButtonsWrapper>
            {isNextMonthFutureDate(endDate) &&
              isCommercialOrAcademicOrFreeType(licenseType) &&
              !isRenewalActionNone && (
                <ProductAdminLink
                  domain={domain}
                  accountName={accountName}
                  productKey={productKey}
                  hasExternalUuid={hasExternalUuid}
                />
              )}
            {isCommercialOrAcademicType(licenseType) &&
              (isNextMonthPastDate(endDate) || isRenewalActionNone) && <ExpiredMessage />}
            {!notPartOfForRenewKeys.includes(productKey) &&
              (isSupportOnlyProductKey(productKey) || isRenewableAccount) &&
              monthsValid > 1 && <RenewLink accountId={accountId} />}
            <ArchiveUnarchiveButtonLink
              isArchiveButtonVisible={isArchiveButtonVisible}
              isArchived={isArchived}
              accountId={accountId}
              isAccount={isAccount}
            />
          </StyledButtonsWrapper>
        </StyledFlex>
      </>
    );
  }

  return (
    <>
      {isSiteOnConcession && <FreeExtensionAppliedMessage />}
      <StyledFlex>
        <ActionsMessage />
        <StyledButtonsWrapper>
          <ArchiveUnarchiveButtonLink
            isArchiveButtonVisible={isArchiveButtonVisible}
            isArchived={isArchived}
            accountId={accountId}
            isAccount={isAccount}
          />
        </StyledButtonsWrapper>
      </StyledFlex>
    </>
  );
};
